import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { FaCashRegister } from 'react-icons/fa';
import { toastSuccess, toastError } from '../utils/toasts';
import {
  useGetBusinessCashRegistersQuery, useCreateEventCashRegisterMutation,
  useOpenEventCashRegisterMutation, useCloseEventCashRegisterMutation,
  useUpdateEventCashRegisterMutation,
  useDeleteEventCashRegisterMutation,
  RootState,
} from '../store';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import CustomModal from './atoms/CustomModal';
import Button from './atoms/Button';
import Checkbox from './atoms/Checkbox';
import { BusinessCashRegisterDTO } from '../models/business';
import MenuDropdown from './atoms/MenuDropdown';
import Input from './atoms/Input';
import Switch from './atoms/Switch';
import LoadingLottie from './atoms/LoadingLottie';
import EventDTO, { EventCashRegisterDTO } from '../models/event';
import CurrencyInput from './atoms/CurrencyInput';
import useGetUserRole from '../hooks/useGetUserRole';

interface TableRow extends BaseData {
  name: string;
  real_status: string;
  status: JSX.Element;
  users_can_access_reports: string;
  cash_register_id: string | JSX.Element;
}

interface OpenFormValues {
  initial_amount: number;
}

interface CloseFormValues {
  closing_amount: number;
}

interface EditFormValues {
  users_can_access_reports: boolean;
}

interface ModalState {
  type: 'open' | 'close' | 'edit' | 'delete' | 'manage' | null;
  data?: TableRow;
}

interface EventCashRegisterTableProps {
  event?: EventDTO;
  eventCashRegisters?: EventCashRegisterDTO[];
  isLoadingEventCashRegisters?: boolean;
}

const EventCashRegisterTable = ({ event, eventCashRegisters, isLoadingEventCashRegisters }: EventCashRegisterTableProps) => {
  const navigate = useNavigate();
  const { business_id: businessId, event_id: eventId } = useParams();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: businessCashRegisters } = useGetBusinessCashRegistersQuery({
    businessId: businessId || '',
  }, { skip: !businessId });
  const [createEventCashRegister, { isSuccess: isSuccessCreateEventCashRegister, isLoading: isLoadingCreateEventCashRegister }] = useCreateEventCashRegisterMutation();
  const [openEventCashRegister, { isSuccess: isSuccessOpenEventCashRegister, isLoading: isLoadingOpenEventCashRegister }] = useOpenEventCashRegisterMutation();
  const [closeEventCashRegister, { isSuccess: isSuccessCloseEventCashRegister, isLoading: isLoadingCloseEventCashRegister }] = useCloseEventCashRegisterMutation();
  const [updateEventCashRegister, { isSuccess: isSuccessUpdateEventCashRegister, isLoading: isLoadingUpdateEventCashRegister }] = useUpdateEventCashRegisterMutation();
  const [deleteEventCashRegister, { isSuccess: isSuccessDeleteEventCashRegister, isLoading: isLoadingDeleteEventCashRegister }] = useDeleteEventCashRegisterMutation();
  const [showModal, setShowModal] = useState(false);
  const [currentModal, setCurrentModal] = useState<ModalState>({ type: null, data: undefined });
  const [initialCashRegisters, setInitialCashRegisters] = useState<BusinessCashRegisterDTO[]>([]);
  const [selectedCashRegisters, setSelectedCashRegisters] = useState<BusinessCashRegisterDTO[]>([]);
  const role = useGetUserRole();

  useEffect(() => {
    if (eventCashRegisters && businessCashRegisters) {
      setInitialCashRegisters(
        eventCashRegisters.map((cashRegister) => {
          const businessCashRegister = businessCashRegisters?.find((c) => c.id === cashRegister.cash_register_id);
          if (businessCashRegister) {
            return businessCashRegister;
          }
          return undefined;
        }).filter((cr): cr is BusinessCashRegisterDTO => cr !== undefined),
      );
    }
  }, [eventCashRegisters, businessCashRegisters]);

  useEffect(() => {
    if (isSuccessCreateEventCashRegister) {
      toastSuccess('Caja registradora creada correctamente');
      setShowModal(false);
      setCurrentModal({ type: null });
    }
  }, [isSuccessCreateEventCashRegister]);

  useEffect(() => {
    if (isSuccessOpenEventCashRegister) {
      toastSuccess('Caja registradora abierta correctamente');
      setShowModal(false);
      setCurrentModal({ type: null });
    }
  }, [isSuccessOpenEventCashRegister]);

  useEffect(() => {
    if (isSuccessCloseEventCashRegister) {
      toastSuccess('Caja registradora cerrada correctamente');
      setShowModal(false);
      setCurrentModal({ type: null });
    }
  }, [isSuccessCloseEventCashRegister]);

  useEffect(() => {
    if (isSuccessUpdateEventCashRegister) {
      toastSuccess('Caja registradora actualizada correctamente');
      setShowModal(false);
      setCurrentModal({ type: null });
    }
  }, [isSuccessUpdateEventCashRegister]);

  useEffect(() => {
    if (isSuccessDeleteEventCashRegister) {
      toastSuccess('Caja registradora eliminada correctamente');
      setShowModal(false);
      setCurrentModal({ type: null });
    }
  }, [isSuccessDeleteEventCashRegister]);

  const handleSave = async () => {
    try {
      await Promise.all(
        selectedCashRegisters.map((cashRegister) => createEventCashRegister({
          businessId: businessId || '',
          eventId: eventId || '',
          cashRegisterData: {
            cash_register_id: cashRegister.id,
            is_active: true,
            users_can_access_reports: true,
          },
        }).unwrap()),
      );
      setSelectedCashRegisters([]);
      setShowModal(false);
      setCurrentModal({ type: null });
    } catch (error) {
      console.error('Failed to create cash register:', error);
      toastError('Error al crear la caja registradora');
    }
  };

  const handleOpen = async (row: TableRow, data: OpenFormValues) => {
    try {
      const response = await openEventCashRegister({
        businessId: businessId || '',
        eventId: eventId || '',
        cashRegisterId: row.id,
        initialBalance: data.initial_amount || 0,
      }).unwrap();
      console.log(response);
      console.log('Open cash register response:', response.data);
      navigate(`${row.id}/${response.data?.cash_register_session.id}`);
    } catch (error) {
      console.error('Failed to open cash register:', error);
      toastError('Error al abrir la caja registradora');
    }
  };

  const handleClose = (row: TableRow, data: CloseFormValues) => {
    closeEventCashRegister({
      businessId: businessId || '',
      eventId: eventId || '',
      cashRegisterId: row.id,
      closingBalance: data.closing_amount || 0,
    });
  };

  const handleEdit = (row: TableRow, data: EditFormValues) => {
    updateEventCashRegister({
      businessId: businessId || '',
      eventId: eventId || '',
      cashRegisterId: row.id,
      cashRegisterData: {
        id: row.id,
        users_can_access_reports: data.users_can_access_reports,
      },
    });
  };

  const headCells = [
    {
      id: 'name',
      label: 'Nombre caja',
    },
    {
      id: 'status',
      label: 'Estado',
    },
  ];

  const rows: TableRow[] = eventCashRegisters?.map((cashRegister) => ({
    id: cashRegister.id,
    name: businessCashRegisters?.find((c) => c.id === cashRegister.cash_register_id)?.name || '',
    real_status: cashRegister.status,
    status: cashRegister.status === 'open' ? (
      <div className='tag-primary'>
        Abierta
      </div>
    ) : (
      <div className='tag-warning'>
        Cerrada
      </div>
    ),
    users_can_access_reports: cashRegister.users_can_access_reports ? 'Si' : 'No',
    cash_register_id: cashRegister.cash_register_id,
  })) || [];

  const handleButtonClick = () => {
    setCurrentModal({ type: 'manage' });
    setShowModal(true);
  };

  const customButton = <Button onClick={handleButtonClick} variant='secondary'>
    <div className='flex flex-row gap-2 items-center'>
      <FaCashRegister className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>Gestionar cajas</p>
    </div>
  </Button>;

  const handleOpenCashRegister = (row: TableRow) => {
    setCurrentModal({ type: 'open', data: row });
    setShowModal(true);
  };

  const handleCloseCashRegister = (row: TableRow) => {
    setCurrentModal({ type: 'close', data: row });
    setShowModal(true);
  };

  const handleEditCashRegister = (row: TableRow) => {
    setCurrentModal({ type: 'edit', data: row });
    setShowModal(true);
  };

  const handleDelete = (row: TableRow) => {
    deleteEventCashRegister({
      businessId: businessId || '',
      eventId: eventId || '',
      cashRegisterId: row.id,
    });
  };

  const handleDeleteCashRegister = (row: TableRow) => {
    setCurrentModal({ type: 'delete', data: row });
    setShowModal(true);
  };

  const actions = (row: TableRow) => (
    <MenuDropdown
      options={[
        ...((event?.active && row.real_status === 'close') ? [{
          label: 'Abrir caja',
          onClick: () => handleOpenCashRegister(row),
        }] : []),
        ...((event?.active && row.real_status === 'open') ? [{
          label: 'Cerrar caja',
          onClick: () => handleCloseCashRegister(row),
        }] : []),
        {
          label: 'Ver caja',
          onClick: () => {
            navigate(`${row.id}`);
          },
        },
        ...(role === 'ADMIN' ? [{
          label: 'Editar caja',
          onClick: () => {
            handleEditCashRegister(row);
          },
        }] : []),
        ...(row.real_status === 'close' && role === 'ADMIN' ? [{
          label: 'Eliminar del evento',
          onClick: () => {
            handleDeleteCashRegister(row);
          },
        }] : []),
      ]}
      data={row}
    />
  );

  if (isLoadingEventCashRegisters) {
    return (
      <div>
        <LoadingLottie />
      </div>
    );
  }

  return (
    <div>
      {eventCashRegisters && eventCashRegisters.length > 0 ? <FilterTable
        rows={rows}
        headCells={headCells}
        tableId="cash-registers"
        customButtons={role === 'ADMIN' || role === 'ROOT' ? [customButton] : undefined}
        actions={actions}
        pagination={{ enabled: true, pageSize: 10 }}
      />
        : <EmptyTable
          button={[
            {
              label: 'Gestionar cajas',
              onClick: () => { handleButtonClick(); },
              variant: 'secondary',
              icon: FaCashRegister,
            },
          ]}
          title="No hay cajas registradoras"
          info="Para poder gestionar las cajas registradoras en este evento, primero agregalas usando el botón 'Gestionar cajas'."
          icon={FaCashRegister}
          iconColor="text-primary-500"
        />}
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          setCurrentModal({ type: null });
        }}
        hideCancelButton
      >
        {(() => {
          if (!currentModal.type || !showModal) {
            return null;
          }

          const rowData = currentModal.data;

          if (currentModal.type === 'open' && rowData) {
            const OpenForm = () => {
              const {
                handleSubmit, formState: { errors }, watch, setValue,
              } = useForm<OpenFormValues>({
                defaultValues: { initial_amount: 0 },
              });

              const onSubmit = (data: OpenFormValues) => {
                handleOpen(rowData, data);
              };

              return (
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col max-w-[600px]'>
                  <h1>Abrir caja {rowData.name}</h1>
                  <p className='mb-4'>Ingresá la cantidad de efectivo con el que contás al abrir la caja</p>
                  <div className='flex flex-col gap-4'>
                    <Input label='Usuario' labelAbove id='user' disabled value={user?.username} />
                    <CurrencyInput
                      label='Monto inicial en efectivo'
                      labelAbove
                      id='initial_amount'
                      value={watch('initial_amount')}
                      onChange={(value) => {
                        setValue('initial_amount', value);
                      }}
                      errMsg={errors.initial_amount?.message}
                    />
                  </div>
                  <div className='flex flex-row gap-2 items-center mt-6'>
                    <Button variant='secondary' onClick={() => { setShowModal(false); setCurrentModal({ type: null }); }} type="button">Cancelar</Button>
                    <Button
                      variant='primary'
                      type="submit"
                      loading={isLoadingOpenEventCashRegister}
                    >
                      Abrir caja
                    </Button>
                  </div>
                </form>
              );
            };
            return <OpenForm />;
          }

          if (currentModal.type === 'close' && rowData) {
            const CloseForm = () => {
              const {
                handleSubmit, formState: { errors }, watch, setValue,
              } = useForm<CloseFormValues>({
                defaultValues: { closing_amount: 0 },
              });

              const onSubmit = (data: CloseFormValues) => {
                handleClose(rowData, data);
              };

              return (
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col max-w-[600px]'>
                  <h1>Cerrar caja {rowData.name}</h1>
                  <p className='mb-4'>Ingresá la cantidad de efectivo con el que contás al cerrar la caja</p>
                  <div className='flex flex-col gap-4'>
                    <Input
                      label='Usuario'
                      labelAbove
                      id='user'
                      disabled
                      value={user?.username}
                    />
                    <CurrencyInput
                      label='Monto final en efectivo'
                      labelAbove
                      id='closing_amount'
                      value={watch('closing_amount')}
                      onChange={(value) => {
                        setValue('closing_amount', value);
                      }}
                      errMsg={errors.closing_amount?.message}
                    />
                  </div>
                  <div className='flex flex-row gap-2 items-center mt-6'>
                    <Button variant='secondary' onClick={() => { setShowModal(false); setCurrentModal({ type: null }); }} type="button">Cancelar</Button>
                    <Button
                      variant='primary'
                      type="submit"
                      loading={isLoadingCloseEventCashRegister}
                    >
                      Cerrar caja
                    </Button>
                  </div>
                </form>
              );
            };
            return <CloseForm />;
          }

          if (currentModal.type === 'edit' && rowData) {
            const EditForm = () => {
              const { handleSubmit, control } = useForm<EditFormValues>({
                defaultValues: { users_can_access_reports: rowData.users_can_access_reports === 'Si' },
              });

              const onSubmit = (data: EditFormValues) => {
                handleEdit(rowData, data);
              };

              return (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <h1>Editar caja {rowData.name}</h1>
                  <p>Podés editar el estado y permisos de reportes para esta caja</p>
                  <div className='flex flex-col gap-4 mt-4'>
                    <Controller
                      name="users_can_access_reports"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id='users_can_access_reports'
                          label='Reportes en tiempo real para cajeros'
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Input id='name' label='Nombre caja*' labelAbove value={rowData.name} disabled />
                  </div>
                  <div className='flex flex-row gap-2 items-center mt-6'>
                    <Button variant='secondary' onClick={() => { setShowModal(false); setCurrentModal({ type: null }); }} type="button">Cancelar</Button>
                    <Button variant='primary' type="submit" loading={isLoadingUpdateEventCashRegister}>Guardar</Button>
                  </div>
                </form>
              );
            };
            return <EditForm />;
          }

          if (currentModal.type === 'delete' && rowData) {
            return (
              <div>
                <h1>Eliminar caja</h1>
                <p>¿Estás seguro de querer eliminar esta caja ({rowData.name})?</p>
                <div className='flex flex-row gap-2 items-center mt-6'>
                  <Button variant='secondary' onClick={() => { setShowModal(false); setCurrentModal({ type: null }); }}>Cancelar</Button>
                  <Button variant='primary' onClick={() => handleDelete(rowData)} loading={isLoadingDeleteEventCashRegister}>Eliminar</Button>
                </div>
              </div>
            );
          }

          if (currentModal.type === 'manage') {
            return (
              <div className='flex flex-col max-w-[600px]'>
                <h1>Gestionar cajas</h1>
                <p className='mb-4'>Podrás elegir las cajas previamente creadas desde la productora y que se encuentren en estado activas.</p>
                {businessCashRegisters?.filter((cashRegister) => cashRegister.is_active).map((cashRegister) => (
                  <div className='flex flex-row gap-2 items-center border-b border-border-primary pt-2' key={cashRegister.id}>
                    <Checkbox
                      label={cashRegister.name}
                      disabled={initialCashRegisters.some((cr) => cr.id === cashRegister.id)}
                      isChecked={
                        initialCashRegisters.some((cr) => cr.id === cashRegister.id)
                        || selectedCashRegisters.some((cr) => cr.id === cashRegister.id)
                      }
                      onCheckChange={(isChecked) => {
                        if (isChecked) {
                          setSelectedCashRegisters([...selectedCashRegisters, cashRegister]);
                        } else {
                          setSelectedCashRegisters(selectedCashRegisters.filter((cr) => cr.id !== cashRegister.id));
                        }
                      }}
                    />
                  </div>
                ))}
                <div className='flex flex-row gap-2 items-center mt-6'>
                  <Button variant='secondary' onClick={() => { setShowModal(false); setCurrentModal({ type: null }); }}>Cancelar</Button>
                  <Button variant='primary' onClick={handleSave} loading={isLoadingCreateEventCashRegister}>Guardar</Button>
                </div>
              </div>
            );
          }
          return null;
        })()}
      </CustomModal>
    </div>
  );
};

export default EventCashRegisterTable;
